// Settings → Costs. Tracks API usage and estimated spend (Anthropic tokens,
// ElevenLabs characters). Token/char counts are exact; dollars are estimates.

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface Bucket { costUsd: number; inputTokens: number; outputTokens: number; chars: number; count: number }
interface Period { total: number; anthropic: Bucket; elevenlabs: Bucket }
interface Usage {
  periods: { today: Period; month: Period; all: Period };
  byKind: Array<{ provider: string; kind: string; costUsd: number; count: number; inputTokens: number; outputTokens: number; chars: number }>;
  byOrg?: Array<{ organizationId: string; name: string; costUsd: number; count: number; inputTokens: number; outputTokens: number; chars: number }>;
  trend: Array<{ date: string; costUsd: number }>;
  recent: Array<{ id: string; createdAt: number; provider: string; kind: string; model: string | null; inputTokens: number; outputTokens: number; chars: number; costUsd: number }>;
  rates: { anthropicNote: string; elevenlabsNote: string; disclaimer: string };
}

const usd = (n: number) => (n < 0.01 && n > 0 ? '<$0.01' : `$${n.toFixed(2)}`);
const num = (n: number) => n.toLocaleString();

export function UsagePane() {
  const { data, isLoading } = useQuery<{ usage: Usage }>({
    queryKey: ['usage', 'summary'],
    queryFn: () => api<{ usage: Usage }>('/api/usage/summary'),
    refetchInterval: 30_000,
  });

  if (isLoading || !data) {
    return <div className="text-ink/40 text-[12px]">Loading…</div>;
  }
  const u = data.usage;
  const maxTrend = Math.max(0.01, ...u.trend.map((t) => t.costUsd));

  return (
    <div>
      <div className="mb-5">
        <h3 className="font-serif text-2xl text-ink m-0">API costs</h3>
        <p className="font-serif italic text-sm text-ink/55 mt-1 max-w-prose">
          What the twin spends on external APIs. Token and character counts are exact (from the providers);
          dollar figures are estimates from a local price table.
        </p>
      </div>

      {/* Period totals */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <PeriodCard label="Today" p={u.periods.today} accent />
        <PeriodCard label="This month" p={u.periods.month} />
        <PeriodCard label="All time" p={u.periods.all} />
      </div>

      {/* 14-day trend */}
      <Section label="Daily spend (14 days, estimated)">
        <div className="flex items-end gap-1.5 h-24">
          {u.trend.map((t) => (
            <div key={t.date} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${t.date}: ${usd(t.costUsd)}`}>
              <div className="w-full flex-1 flex items-end">
                <div className="w-full rounded-t bg-gradient-to-t from-amber/40 to-particle/70"
                  style={{ height: `${Math.max(2, (t.costUsd / maxTrend) * 100)}%` }} />
              </div>
              <span className="font-mono text-[7px] text-ink/30">{t.date.slice(5)}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Breakdown by kind */}
      <Section label="By activity">
        {u.byKind.length === 0 ? (
          <p className="font-serif italic text-ink/40 text-sm">No API usage recorded yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {u.byKind.map((k) => (
              <li key={`${k.provider}-${k.kind}`} className="flex items-center gap-3 text-[13px]">
                <span className={`px-1.5 py-0.5 rounded-full border text-[11px] ${ k.provider === 'elevenlabs' ? 'text-sky-300 border-sky-300/30' : 'text-amber border-amber/40' }`}>{k.provider === 'elevenlabs' ? 'eleven' : 'claude'}</span>
                <span className="font-serif text-ink/80 w-32">{k.kind}</span>
                <span className="font-mono text-[11px] text-ink/45 flex-1">
                  {k.provider === 'elevenlabs' ? `${num(k.chars)} chars` : `${num(k.inputTokens)}→${num(k.outputTokens)} tok`}
                  {' · '}{k.count}×
                </span>
                <span className="font-mono text-[12px] text-ink">{usd(k.costUsd)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Breakdown by organization (professional layer — billed to the company) */}
      {(u.byOrg?.length ?? 0) > 0 && (
        <Section label="By organization (billed to the company)">
          <ul className="space-y-1.5">
            {u.byOrg!.map((o) => (
              <li key={o.organizationId} className="flex items-center gap-3 text-[13px]">
                <span className="px-1.5 py-0.5 rounded-full border border-particle/40 text-particle text-[11px]">org</span>
                <span className="font-serif text-ink/80 w-40 truncate">{o.name}</span>
                <span className="font-mono text-[11px] text-ink/45 flex-1">{num(o.inputTokens)}→{num(o.outputTokens)} tok · {o.count}×</span>
                <span className="font-mono text-[12px] text-ink">{usd(o.costUsd)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Recent events */}
      <Section label="Recent calls">
        {u.recent.length === 0 ? (
          <p className="font-serif italic text-ink/40 text-sm">Nothing yet.</p>
        ) : (
          <ul className="space-y-1 max-h-[34vh] overflow-y-auto">
            {u.recent.map((r) => (
              <li key={r.id} className="flex items-center gap-3 px-2 py-1.5 rounded border border-ink/[0.06] text-[11px]">
                <span className="font-mono text-ink/35 w-28">{new Date(r.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                <span className="font-mono text-ink/55 w-24">{r.kind}</span>
                <span className="font-mono text-ink/40 flex-1 truncate">
                  {r.provider === 'elevenlabs' ? `${num(r.chars)} chars` : `${r.model ?? ''} ${num(r.inputTokens)}/${num(r.outputTokens)}`}
                </span>
                <span className="font-mono text-ink/80">{usd(r.costUsd)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <p className="mt-5 font-mono text-[9px] text-ink/35 leading-relaxed">
        {u.rates.disclaimer}<br />{u.rates.anthropicNote}<br />ElevenLabs: {u.rates.elevenlabsNote}
      </p>
    </div>
  );
}

function PeriodCard({ label, p, accent }: { label: string; p: Period; accent?: boolean }) {
  return (
    <div className="p-3 rounded-xl border border-ink/[0.08]">
      <div className="text-[11px] text-ink/40">{label}</div>
      <div className={`font-serif text-3xl mt-1 ${accent ? 'text-amber' : 'text-ink'}`}>{usd(p.total)}</div>
      <div className="font-mono text-[9.5px] text-ink/40 mt-1.5 leading-relaxed">
        claude {usd(p.anthropic.costUsd)} · {(p.anthropic.inputTokens + p.anthropic.outputTokens).toLocaleString()} tok<br />
        eleven {usd(p.elevenlabs.costUsd)} · {p.elevenlabs.chars.toLocaleString()} chars
      </div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="mt-6">
      <div className="text-[11px] text-ink/45 mb-2.5">{label}</div>
      {children}
    </section>
  );
}
